import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Loader2, UserPlus, AlertCircle, CheckCircle2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const JoinPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setAuthData } = useAuth();
    
    const inviteCode = searchParams.get('c');
    
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [inviteInfo, setInviteInfo] = useState(null);
    
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        confirm_password: ''
    });

    useEffect(() => {
        if (inviteCode) {
            validateInvite();
        } else {
            setError('Kein Einladungscode angegeben');
            setLoading(false);
        }
    }, [inviteCode]);

    const validateInvite = async () => {
        try {
            const response = await axios.get(`${API}/user-invites/validate/${inviteCode}`);
            setInviteInfo(response.data);
        } catch (error) {
            console.error('Invalid invite:', error);
            setError('Ungültiger oder abgelaufener Einladungscode');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!formData.first_name.trim() || !formData.last_name.trim()) {
            toast.error('Bitte geben Sie Ihren Namen ein');
            return;
        }
        
        if (!formData.email.trim()) {
            toast.error('Bitte geben Sie Ihre E-Mail-Adresse ein');
            return;
        }
        
        if (formData.password.length < 6) {
            toast.error('Das Passwort muss mindestens 6 Zeichen lang sein');
            return;
        }
        
        if (formData.password !== formData.confirm_password) {
            toast.error('Die Passwörter stimmen nicht überein');
            return;
        }

        setSubmitting(true);
        
        try {
            const response = await axios.post(`${API}/user-invites/signup`, {
                first_name: formData.first_name.trim(),
                last_name: formData.last_name.trim(),
                email: formData.email.trim().toLowerCase(),
                password: formData.password,
                invite_code: inviteCode
            });
            
            // Set auth data and redirect
            const { token, user, organization } = response.data;
            setAuthData(token, user, organization);
            
            toast.success(`Willkommen, ${user.name}!`);
            
            // Redirect to customer dashboard
            navigate(`/o/${organization.organization_code}/dashboard`);
            
        } catch (error) {
            console.error('Signup failed:', error);
            const message = error.response?.data?.detail || 'Registrierung fehlgeschlagen';
            toast.error(message);
            
            // If email already exists, suggest login
            if (message.includes('bereits registriert')) {
                setError('Diese E-Mail-Adresse ist bereits registriert. Bitte melden Sie sich stattdessen an.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
                    <p className="text-slate-400">Einladung wird überprüft...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error && !inviteInfo) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
                <Card className="w-full max-w-md border-0 shadow-2xl bg-slate-800">
                    <CardContent className="p-8 text-center">
                        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                            <AlertCircle className="h-8 w-8 text-red-400" />
                        </div>
                        <h2 className="text-xl font-semibold text-white mb-2">
                            Einladung ungültig
                        </h2>
                        <p className="text-slate-400 mb-6">{error}</p>
                        <Button
                            variant="outline"
                            className="border-slate-600 text-slate-300"
                            onClick={() => navigate('/login')}
                        >
                            Zur Anmeldung
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const org = inviteInfo?.organization;
    const primaryColor = org?.primary_color || '#3b82f6';

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
            <div className="w-full max-w-lg">
                {/* Organization Header */}
                <div className="text-center mb-8">
                    {org?.logo ? (
                        <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${org.logo}`}
                            alt={org.name}
                            className="h-16 mx-auto mb-4 object-contain"
                        />
                    ) : (
                        <div 
                            className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                            style={{ backgroundColor: `${primaryColor}20` }}
                        >
                            <Building2 className="h-8 w-8" style={{ color: primaryColor }} />
                        </div>
                    )}
                    <h1 className="text-2xl font-bold text-white mb-2">
                        {org?.name}
                    </h1>
                    <p className="text-slate-400">
                        Sie wurden von <span className="text-white">{inviteInfo?.invited_by?.name}</span> eingeladen
                    </p>
                </div>

                <Card className="border-0 shadow-2xl bg-slate-800">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-xl text-white flex items-center gap-2">
                            <UserPlus className="h-5 w-5" style={{ color: primaryColor }} />
                            Registrierung
                        </CardTitle>
                        <CardDescription className="text-slate-400">
                            Erstellen Sie Ihr Konto, um am Empfehlungsprogramm teilzunehmen
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {error && (
                            <div className="mb-4 p-3 bg-amber-500/20 border border-amber-500/50 rounded-lg flex items-start gap-2">
                                <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-amber-200 text-sm">{error}</p>
                                    <Button
                                        variant="link"
                                        className="text-amber-400 p-0 h-auto text-sm"
                                        onClick={() => navigate(`/o/${org?.organization_code}/login`)}
                                    >
                                        Zur Anmeldung →
                                    </Button>
                                </div>
                            </div>
                        )}
                        
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="first_name" className="text-slate-200">Vorname *</Label>
                                    <Input
                                        id="first_name"
                                        value={formData.first_name}
                                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                        placeholder="Max"
                                        className="bg-slate-700 border-slate-600 text-white"
                                        data-testid="first-name-input"
                                        disabled={submitting}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="last_name" className="text-slate-200">Nachname *</Label>
                                    <Input
                                        id="last_name"
                                        value={formData.last_name}
                                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                        placeholder="Mustermann"
                                        className="bg-slate-700 border-slate-600 text-white"
                                        data-testid="last-name-input"
                                        disabled={submitting}
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-slate-200">E-Mail *</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="max@beispiel.de"
                                    className="bg-slate-700 border-slate-600 text-white"
                                    data-testid="email-input"
                                    disabled={submitting}
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-slate-200">Passwort *</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    placeholder="Mindestens 6 Zeichen"
                                    className="bg-slate-700 border-slate-600 text-white"
                                    data-testid="password-input"
                                    disabled={submitting}
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="confirm_password" className="text-slate-200">Passwort bestätigen *</Label>
                                <Input
                                    id="confirm_password"
                                    type="password"
                                    value={formData.confirm_password}
                                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                                    placeholder="Passwort wiederholen"
                                    className="bg-slate-700 border-slate-600 text-white"
                                    data-testid="confirm-password-input"
                                    disabled={submitting}
                                />
                            </div>

                            <Button
                                type="submit"
                                className="w-full"
                                style={{ backgroundColor: primaryColor }}
                                disabled={submitting}
                                data-testid="signup-submit-btn"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Registrieren...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="mr-2 h-4 w-4" />
                                        Registrieren
                                    </>
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                <p className="text-center text-sm text-slate-500 mt-6">
                    Sie haben bereits ein Konto?{' '}
                    <Button
                        variant="link"
                        className="text-blue-400 p-0 h-auto"
                        onClick={() => navigate(`/o/${org?.organization_code}/login`)}
                    >
                        Jetzt anmelden
                    </Button>
                </p>
            </div>
        </div>
    );
};
